import React from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/**
 * Overview map for a day's GPS punches. Renders each punch as a numbered pin,
 * connected by a polyline showing the chronological route. Uses free
 * OpenStreetMap tiles — no API key needed.
 *
 * Props:
 *   points: [{ id, lat, lng, action, timestamp }]
 */
export default function DayPunchesMap({ points = [] }) {
  // Only keep points with valid coordinates
  const valid = (points || []).filter(
    (p) => typeof p?.lat === "number" && typeof p?.lng === "number"
  );
  if (valid.length === 0) return null;

  // Compute center + bounds so the map auto-fits all points
  const lats = valid.map((p) => p.lat);
  const lngs = valid.map((p) => p.lng);
  const center = [
    (Math.min(...lats) + Math.max(...lats)) / 2,
    (Math.min(...lngs) + Math.max(...lngs)) / 2,
  ];
  const bounds = valid.length > 1
    ? [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]]
    : null;

  const route = valid.map((p) => [p.lat, p.lng]);

  return (
    <div className="rounded-xl overflow-hidden border border-slate-200" data-testid="day-punches-map">
      <MapContainer
        center={center}
        zoom={15}
        bounds={bounds || undefined}
        boundsOptions={{ padding: [30, 30] }}
        style={{ height: 280, width: "100%" }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {valid.length > 1 && <Polyline positions={route} pathOptions={{ color: "#0284c7", weight: 3, opacity: 0.7 }} />}
        {valid.map((p, idx) => (
          <Marker
            key={p.id || idx}
            position={[p.lat, p.lng]}
            icon={numberedIcon(idx + 1, p.action)}
          >
            <Popup>
              <div className="text-xs">
                <p className="font-bold capitalize">{(p.action || "punch").replace(/_/g, " ")}</p>
                <p className="text-slate-600">{p.lat.toFixed(5)}, {p.lng.toFixed(5)}</p>
                {p.timestamp && <p className="text-slate-500">{new Date(p.timestamp).toLocaleTimeString()}</p>}
                <a
                  href={`https://www.google.com/maps?q=${p.lat},${p.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sky-700 underline"
                >Open in Google Maps</a>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}

// Build a small numbered HTML pin colored by the punch action
function numberedIcon(n, action) {
  const color =
    action?.includes("clock_in") ? "#16a34a" :   // green
    action?.includes("clock_out") ? "#dc2626" :  // red
    action?.includes("lunch") ? "#d97706" :      // amber
    "#0284c7";                                    // sky
  return L.divIcon({
    className: "",
    html: `
      <div style="
        background:${color};
        color:#fff;
        font-size:11px;
        font-weight:700;
        width:24px;
        height:24px;
        border-radius:50%;
        display:flex;
        align-items:center;
        justify-content:center;
        box-shadow:0 2px 4px rgba(0,0,0,0.3);
        border:2px solid #fff;
      ">${n}</div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
}
